import type { DrinkActivitySummary } from "../../../services/drinkService";
import { calculatePureAlcoholGrams } from "./utils";
import { DEFAULT_DRINK_DURATION_MINUTES, DRINKING_DAY_PADDING_MINUTES, DRINKING_DAY_SHIFT_HOURS } from "./constants";

const MS_IN_DAY = 24 * 60 * 60 * 1000;
const DRINKING_SESSION_GAP_MS = DRINKING_DAY_SHIFT_HOURS * 60 * 60 * 1000;
const DRINKING_DAY_PADDING_MS = DRINKING_DAY_PADDING_MINUTES * 60 * 1000;

export const getLatestActivityTimestamp = (activities: DrinkActivitySummary[]) =>
    activities.reduce<number | null>((latest, activity) => {
        const timestamp = activity.timestamp?.getTime();
        return typeof timestamp === "number" && (latest === null || timestamp > latest) ? timestamp : latest;
    }, null);

export const selectActivitiesForDay = (
    activities: DrinkActivitySummary[],
    windowStart: number,
    windowEnd: number
) => {
    const indexed = activities
        .map((activity, index) => ({ index, finishMs: activity.timestamp?.getTime() }))
        .filter((entry): entry is { index: number; finishMs: number } => typeof entry.finishMs === "number")
        .sort((a, b) => a.finishMs - b.finishMs);
    const sessions: { indexes: number[]; endMs: number; hasWindowActivity: boolean }[] = [];

    indexed.forEach(({ index, finishMs }) => {
        const lastSession = sessions.at(-1);
        if (!lastSession || finishMs - lastSession.endMs > DRINKING_SESSION_GAP_MS) {
            sessions.push({ indexes: [index], endMs: finishMs, hasWindowActivity: finishMs >= windowStart && finishMs < windowEnd });
            return;
        }
        lastSession.indexes.push(index);
        lastSession.endMs = finishMs;
        if (finishMs >= windowStart && finishMs < windowEnd) lastSession.hasWindowActivity = true;
    });

    const included = new Set(sessions.filter((session) => session.hasWindowActivity).flatMap((session) => session.indexes));
    return activities.filter((_activity, index) => included.has(index)).sort(
        (a, b) => (a.timestamp?.getTime() ?? 0) - (b.timestamp?.getTime() ?? 0)
    );
};

export const getSelectedDayBounds = (
    activities: DrinkActivitySummary[],
    rangeStart: number,
    rangeEnd: number
) => {
    if (!activities.length) return { start: rangeStart, end: rangeEnd };
    let earliestStart: number | null = null;
    let latestEnd: number | null = null;
    activities.forEach((activity) => {
        const finish = activity.timestamp?.getTime();
        if (typeof finish !== "number") return;
        const duration = activity.durationMinutes != null && activity.durationMinutes >= 0
            ? activity.durationMinutes : DEFAULT_DRINK_DURATION_MINUTES;
        const start = activity.startedAt?.getTime() ?? (duration > 0 ? finish - duration * 60 * 1000 : finish);
        if (earliestStart === null || start < earliestStart) earliestStart = start;
        if (latestEnd === null || finish > latestEnd) latestEnd = finish;
    });
    if (earliestStart === null || latestEnd === null) return { start: rangeStart, end: rangeEnd };
    return {
        start: earliestStart - DRINKING_DAY_PADDING_MS,
        end: Math.max(latestEnd + DRINKING_DAY_PADDING_MS, rangeEnd),
    };
};

export const calculateAlcoholTotals = (
    activities: DrinkActivitySummary[],
    dayStartMs: number,
    dayEndMs: number
) => {
    const totals = { day: 0, week: 0, month: 0 };
    activities.forEach((activity) => {
        const timestamp = activity.timestamp?.getTime();
        if (typeof timestamp !== "number") return;
        const grams = calculatePureAlcoholGrams(activity.amountMl, activity.promille);
        if (timestamp >= dayStartMs && timestamp < dayEndMs) totals.day += grams;
        if (timestamp >= dayStartMs - 6 * MS_IN_DAY && timestamp < dayEndMs) totals.week += grams;
        if (timestamp >= dayStartMs - 29 * MS_IN_DAY && timestamp < dayEndMs) totals.month += grams;
    });
    return {
        day: Number(totals.day.toFixed(1)),
        week: Number(totals.week.toFixed(1)),
        month: Number(totals.month.toFixed(1)),
    };
};

export const computeBreathalyzerEstimate = (promille: number) => {
    const adjustedMgL = Math.max(0, promille / 2.1 - 0.03);
    return {
        breathMgL: Number(adjustedMgL.toFixed(3)),
        policePromille: Number((adjustedMgL * 2.1).toFixed(3)),
    };
};
